import React, { useMemo, useState } from "react";
import { Copy, Check, MessageCircle, Mail, X, Link as LinkIcon, CreditCard } from "lucide-react";

/**
 * Modal per generare un "Pay-by-Link" Nexi pre-compilato da una submission CRM.
 * Crea URL /paga?importo=...&email=...&nome=...&tipo=...&num_contratto=...&submission_id=...
 * Permette: copia link, apri WhatsApp con testo pronto, apri email.
 */
export default function PaymentLinkGenerator({ submission, onClose }) {
  const sub = submission || {};
  const contact = sub.contact || {};
  const [amount, setAmount] = useState("");
  const [numContratto, setNumContratto] = useState("");
  const [description, setDescription] = useState(() => {
    const ramo = sub.type || "polizza";
    return `Pagamento polizza ${ramo} - Colucci Assicuratori`;
  });
  const [copied, setCopied] = useState(false);

  const amt = parseFloat(amount);
  const isValid = Number.isFinite(amt) && amt >= 0.5 && !!contact.email;

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const link = useMemo(() => {
    if (!isValid) return "";
    const params = new URLSearchParams({
      importo: amt.toFixed(2),
      email: contact.email || "",
      nome: [contact.nome, contact.cognome].filter(Boolean).join(" "),
      tipo: sub.type || "generico",
      submission_id: sub.id || "",
      descrizione: description,
    });
    if (numContratto) params.append("num_contratto", numContratto);
    return `${origin}/paga?${params.toString()}`;
  }, [isValid, amt, contact.email, contact.nome, contact.cognome, sub.type, sub.id, numContratto, description, origin]);

  const clienteNome = (contact.nome || "").trim() || "Gentile cliente";
  const whatsappText = `Ciao ${clienteNome},\nCome concordato, ecco il link sicuro per pagare la polizza di € ${isValid ? amt.toFixed(2) : "___"}:\n${link}\n\nIl pagamento e' protetto da Nexi XPay (circuiti Visa, Mastercard, AmEx).\n\nGrazie,\nColucci Assicuratori`;
  const waUrl = contact.telefono
    ? `https://wa.me/${(contact.telefono || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(whatsappText)}`
    : `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
  const mailto = `mailto:${contact.email || ""}?subject=${encodeURIComponent("Link di pagamento polizza - Colucci Assicuratori")}&body=${encodeURIComponent(whatsappText)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="paybylink-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-bold text-zinc-900">Genera link di pagamento</h3>
              <p className="text-xs text-zinc-500">Pay-by-Link Nexi XPay</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-lg" data-testid="paybylink-close">
            <X className="w-4 h-4 text-zinc-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 text-sm bg-zinc-50 rounded-xl px-3 py-2 border border-zinc-100">
            <div className="flex-1">
              <p className="text-zinc-900 font-medium">{[contact.nome, contact.cognome].filter(Boolean).join(" ") || "—"}</p>
              <p className="text-xs text-zinc-500">{contact.email || "no email"}{contact.telefono ? ` · ${contact.telefono}` : ""}</p>
            </div>
            <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-zinc-200 text-zinc-700 capitalize">{sub.type || "generico"}</span>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-zinc-700 mb-1.5 block">
              Importo (€) <span className="text-red-500">*</span>
            </span>
            <input
              type="number"
              step="0.01"
              min="0.50"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="es. 249.50"
              className="w-full border border-zinc-200 focus:border-amber-400 rounded-lg px-3 py-2 outline-none"
              data-testid="paybylink-amount"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-zinc-700 mb-1.5 block">Numero contratto (opzionale)</span>
            <input
              type="text"
              value={numContratto}
              onChange={(e) => setNumContratto(e.target.value)}
              placeholder="es. 2026/0042"
              className="w-full border border-zinc-200 focus:border-amber-400 rounded-lg px-3 py-2 outline-none"
              data-testid="paybylink-contratto"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-zinc-700 mb-1.5 block">Descrizione</span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-zinc-200 focus:border-amber-400 rounded-lg px-3 py-2 outline-none"
              data-testid="paybylink-desc"
            />
          </label>

          {isValid ? (
            <>
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1.5">
                  <LinkIcon className="w-3 h-3" />
                  Link generato
                </div>
                <p className="text-xs text-zinc-700 break-all font-mono leading-relaxed" data-testid="paybylink-url">{link}</p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={handleCopy}
                  className={
                    "inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium border transition-all " +
                    (copied
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50")
                  }
                  data-testid="paybylink-copy"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copiato!" : "Copia"}
                </button>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium bg-[#25D366] text-white hover:bg-[#1faf55] transition-all"
                  data-testid="paybylink-whatsapp"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  WhatsApp
                </a>
                <a
                  href={mailto}
                  className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium bg-zinc-900 text-white hover:bg-zinc-800 transition-all"
                  data-testid="paybylink-email"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Email
                </a>
              </div>

              <p className="text-[11px] text-zinc-400 leading-relaxed">
                Quando il cliente completa il pagamento, la submission #{sub.id?.slice(0, 8)} viene automaticamente marcata come pagata con importo, data e codice autorizzazione Nexi.
              </p>
            </>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {!contact.email ? "Email cliente mancante: aggiorna il profilo del lead." : "Inserisci un importo valido (min €0.50)"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
